import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Card, DataTable, Button, Input, StatusBadge, PageHeader, DatePicker, SectionTabs } from '@/shared/ui'
import { useUsers, usePagination, useDebounce, useRegions, useTransportTypesList } from '@/shared/hooks'
import { USER_STATUS, USER_ROLE, type ApiError } from '@/shared/api'
import type { UserListItemResponse } from '@/shared/api'

// Границы суток считаем в московском времени (UTC+3), независимо от таймзоны
// браузера, так как список отображает created_at в МСК.
// created_from — начало мсковских суток, created_to — начало следующих суток
// (исключительно), чтобы диапазон включал весь выбранный день.
const dayStartMsk = (d: Date): number =>
  Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 1000) - 3 * 3600
const dayEndMskExclusive = (d: Date): number => dayStartMsk(d) + 86400

export const CouriersPage: React.FC = () => {
  const navigate = useNavigate()
  const { page, limit, setPage } = usePagination({ initialLimit: 50 })
  
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<number | ''>('')
  const [roleFilter, setRoleFilter] = useState<number | ''>('')
  const [regionFilter, setRegionFilter] = useState<number | ''>('')
  const [transportFilter, setTransportFilter] = useState<number | ''>('')
  const [createdFrom, setCreatedFrom] = useState<Date | null>(null)
  const [createdTo, setCreatedTo] = useState<Date | null>(null)
  
  const debouncedSearch = useDebounce(search, 500)

  // При изменении поиска (после debounce) возвращаемся на первую страницу,
  // иначе при сужении выборки с page > 1 таблица может оказаться пустой.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, setPage])

  // Справочники для фильтров
  const { data: regions } = useRegions()
  const { data: transportTypes } = useTransportTypesList({ page: 1, limit: 100 })

  // Параметры запроса
  const queryParams = useMemo(() => ({
    page,
    limit,
    search: debouncedSearch || undefined,
    status: statusFilter ? Number(statusFilter) : undefined,
    role: roleFilter ? Number(roleFilter) : undefined,
    region_id: regionFilter ? Number(regionFilter) : undefined,
    transport_type_id: transportFilter ? Number(transportFilter) : undefined,
    created_from: createdFrom ? dayStartMsk(createdFrom) : undefined,
    created_to: createdTo ? dayEndMskExclusive(createdTo) : undefined,
  }), [page, limit, debouncedSearch, statusFilter, roleFilter, regionFilter, transportFilter, createdFrom, createdTo])

  const { data, isLoading, error } = useUsers(queryParams)

  // Колонки таблицы
  const columns = [
    {
      key: 'user_id' as const,
      title: 'ID',
      width: '16',
    },
    {
      key: 'full_name' as const,
      title: 'ФИО',
      render: (_: any, user: UserListItemResponse) => (
        <div>
          <div className="font-medium text-gray-900">{user.full_name}</div>
          {user.email && (
            <div className="text-sm text-gray-500">{user.email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'phone' as const,
      title: 'Телефон',
      render: (phone: string) => phone || '—',
    },
    {
      key: 'role' as const,
      title: 'Роль',
      render: (_: any, user: UserListItemResponse) => (
        <StatusBadge status={user.role} type="role" />
      ),
    },
    {
      key: 'status' as const,
      title: 'Статус',
      render: (_: any, user: UserListItemResponse) => (
        <StatusBadge status={user.status} type="status" />
      ),
    },
    {
      key: 'region_name' as const,
      title: 'Регион',
      render: (region: string) => region || '—',
    },
    {
      key: 'created_at_formatted' as const,
      title: 'Дата создания',
    },
  ]

  const handleRowClick = (user: UserListItemResponse) => {
    navigate({ to: '/couriers/$userId', params: { userId: user.user_id.toString() } })
  }

  if (error) {
    const apiError = error as unknown as ApiError
    return (
      <>
        <PageHeader 
          title="Курьеры" 
          description="Управление курьерами системы" 
        />
        <div className="flex-1 p-6">
          <Card>
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-red-600 mb-2">
                Ошибка загрузки данных
              </h3>
              <p className="text-gray-500">{apiError.detail || 'Произошла ошибка'}</p>
            </div>
          </Card>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader 
        title="Курьеры" 
        description="Управление курьерами системы" 
      />
      
      <div className="px-6 pt-4">
        <SectionTabs
          items={[
            { label: 'Все курьеры', to: '/couriers' },
            { label: 'На модерации', to: '/moderation' },
          ]}
        />
      </div>
      
      <div className="flex-1 p-6 overflow-hidden flex flex-col min-h-0">
        {/* Фильтры */}
        <Card className="mb-6">
          <div className="space-y-4">
            {/* Текстовые фильтры и справочники */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <Input
                placeholder="Поиск по имени, email, телефону..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <select
                className="admin-input"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value ? Number(e.target.value) : '')
                  setPage(1)
                }}
              >
                <option value="">Все статусы</option>
                <option value={USER_STATUS.ACTIVE}>Активные</option>
                <option value={USER_STATUS.BLOCKED}>Заблокированные</option>
                <option value={USER_STATUS.DELETED}>Удаленные</option>
              </select>

              <select
                className="admin-input"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value ? Number(e.target.value) : '')
                  setPage(1)
                }}
              >
                <option value="">Все роли</option>
                <option value={USER_ROLE.COURIER}>Курьеры</option>
                <option value={USER_ROLE.EXTERNAL_COURIER}>Сторонние курьеры</option>
                <option value={USER_ROLE.ADMIN}>Администраторы</option>
                <option value={USER_ROLE.DEFAULT_USER}>Пользователи</option>
              </select>

              <select
                className="admin-input"
                value={regionFilter}
                onChange={(e) => {
                  setRegionFilter(e.target.value ? Number(e.target.value) : '')
                  setPage(1)
                }}
              >
                <option value="">Все регионы</option>
                {(regions || []).map((region) => (
                  <option key={region.id} value={region.id}>{region.name}</option>
                ))}
              </select>

              <select
                className="admin-input"
                value={transportFilter}
                onChange={(e) => {
                  setTransportFilter(e.target.value ? Number(e.target.value) : '')
                  setPage(1)
                }}
              >
                <option value="">Все виды транспорта</option>
                {(transportTypes?.items || []).map((transport) => (
                  <option key={transport.transport_type_id} value={transport.transport_type_id}>
                    {transport.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Дата создания: с / по */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500">Создан с</label>
                <DatePicker
                  value={createdFrom}
                  onChange={(date) => {
                    setCreatedFrom(date)
                    setPage(1)
                  }}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500">Создан по</label>
                <DatePicker
                  value={createdTo}
                  onChange={(date) => {
                    setCreatedTo(date)
                    setPage(1)
                  }}
                />
              </div>

              <div className="flex items-end">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearch('')
                    setStatusFilter('')
                    setRoleFilter('')
                    setRegionFilter('')
                    setTransportFilter('')
                    setCreatedFrom(null)
                    setCreatedTo(null)
                    setPage(1)
                  }}
                >
                  Сбросить
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Таблица */}
        <DataTable
          data={data?.items || []}
          columns={columns}
          loading={isLoading}
          pagination={data ? {
            page: data.page,
            limit: data.limit,
            total: data.total,
            pages: data.pages,
            onPageChange: setPage,
          } : undefined}
          onRowClick={handleRowClick}
          emptyMessage="Пользователи не найдены"
          fillHeight
          className="flex-1 min-h-0"
        />
      </div>
    </>
  )
}
